/**
 * Pure all-in-total selectors (TRAV-4298).
 *
 * Deliberately import-free: `HotelOffers.utils.ts` and other non-React modules read from here, and
 * pulling the feature-flag barrel into them would drag `AuthProvider` → `@shared/core` → i18n into
 * plain utility graphs. The React side lives in `useAllInTotal.ts`.
 */

/** The shape every travel money field shares, whether it is an `*Amount` or a `*Rate`. */
export type DisplayMonetaryValue = Readonly<{ valueInMinorUnits: number }>;

/**
 * Picks the whole-stay total a guest-facing surface should render.
 *
 * FOR DISPLAY AND DISPLAY ORDERING ONLY. Never pass the result to a charge, a price check, a
 * provider comparison or a margin calculation — `totalAmount` / `totalRate` remain the money
 * fields, and the backend deliberately kept them byte-for-byte unchanged so they still are.
 *
 * `allInTotal*` is non-null and floored at the collected total, so it already equals the collected
 * total when no property-paid fee could be determined; the null guard here is only for callers
 * whose data source predates the field.
 */
export const pickDisplayTotal = <T extends DisplayMonetaryValue>(collectedTotal: T, allInTotal: Maybe<T> | undefined, isAllInEnabled: boolean): T =>
  isAllInEnabled && allInTotal ? allInTotal : collectedTotal;

/**
 * String counterpart of `pickDisplayTotal`, for surfaces that only ever hold a formatted total.
 *
 * The search console formats in `useListingPriceMapping` and passes strings down, so it can never
 * call `pickDisplayTotal`. Same display-only rule, and the same fallback: an absent all-in string
 * (control arm, or a data source predating the field) keeps today's collected string.
 */
export const pickDisplayTotalString = (collectedTotalString: Maybe<string> | undefined, allInTotalString: Maybe<string> | undefined, isAllInEnabled: boolean): string | undefined =>
  ((isAllInEnabled && allInTotalString) || collectedTotalString) ?? undefined;

/**
 * Whether a pay-at-property charge was actually determined, given the amount on its own.
 *
 * The search console divides minor units down to a plain major-unit number before this rule ever
 * runs, so it holds no `DisplayMonetaryValue`. The invariant lives here once for both shapes:
 * absent means "not determined" — carrying the GraphQL null's meaning — and zero is not a charge.
 */
export const hasOnsiteChargesValue = (onsiteChargesTotal: Maybe<number> | undefined): boolean => !!onsiteChargesTotal && onsiteChargesTotal > 0;

/**
 * Whether a pay-at-property charge was actually determined for this offer/pricing.
 *
 * Null means "not determined", NOT zero — so copy must not promise property charges are included
 * when this is false, and no surface may render "$0 due at property" off the back of it.
 */
export const hasOnsiteCharges = (onsiteChargesTotal: Maybe<DisplayMonetaryValue> | undefined): boolean => hasOnsiteChargesValue(onsiteChargesTotal?.valueInMinorUnits);
